import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';
import App from '@/App';
import * as Sentry from '@sentry/react';
import ErrorBoundary from '@/components/ErrorBoundary';
import PageRoutes from '@/lib/routing/PageRoutes';
import { getEnv } from '@/lib/utils/getEnv';
import { isLocalHost } from '@/lib/utils/isLocalHost';
import './i18n';
import './index.css';

if (!isLocalHost) {
  Sentry.init({
    dsn: 'https://2ed39e07489da1ebc773637c6fcd644d@o879385.ingest.us.sentry.io/4508421085921280',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false }),
      Sentry.replayCanvasIntegration(),
    ],
    release: import.meta.env.VITE_APP_VERSION,
    environment: getEnv(),
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/], // TODO replace with your server URL
    replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

// Create an error element for the router
const RouterErrorElement = () => (
  <ErrorBoundary>
    <div>Redirecting...</div>
  </ErrorBoundary>
);

// Router
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <RouterErrorElement />,
      children: Object.values(PageRoutes),
    },
  ],
  { basename: import.meta.env.BASE_URL }
);

// Create React root
createRoot(document.getElementById('root') as HTMLElement).render(
  <section>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </section>
);
